import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Loading } from "../../../components/Loading";
import Select from "../../../components/input/Select";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import { useGetCourseWritingActivityQuery } from "../writingApiSlice";
import CourseWritingActivityTable from "./CourseWritingActivityTable";

interface Props {
	selectedPromptId?: number;
	sortField?: string;
	expandedStatusFilter: string;
	setSelectedPromptId: (id?: number) => void;
	setExpandedStatusFilter: (filter: string) => void;
}

export default function CourseWritingActivity(props: Props) {
	const [writerId, setWriterId] = useState<string>();
	const [writers, setWriters] = useState<SimpleOption[]>([]);

	const writingActivityResult = useGetCourseWritingActivityQuery(
		writerId ? Number(writerId) : null
	);
	const data = writingActivityResult.data;

	const navigate = useNavigate();

	useEffect(() => {
		if (!data) return;
		const writers = [...data.writers]
			.sort((a, b) => (a.text > b.text ? 1 : -1))
			.map((w) => {
				return {
					value: w.value.toString(),
					label: w.text,
				} as SimpleOption;
			});
		writers.unshift({ value: "", label: "All Students" } as SimpleOption);
		setWriters(writers);
	}, [data]);

	return data?.writingActivity?.length === 0 && !writerId ? (
		<div className="text-center">
			<h2 className="pt-4 pb-4">Your students have not started writing yet!</h2>
			<h3 className="text-base">Click on the Prompt Library tab to view all prompts.</h3>
			<div className="relative flex py-2 items-center sm:mx-auto sm:w-full sm:max-w-md">
				<div className="grow border-t border-gray-400"></div>
				<span className="shrink mx-4 text-sm">or</span>
				<div className="grow border-t border-gray-400"></div>
			</div>
			<h3 className="pb-4 text-base">Click on the button below to create a new prompt.</h3>
			<Button
				id="create-prompt-button"
				className="btn btn-primary"
				onClick={() => navigate("/writing/prompt")}>
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Create Prompt
				</span>
			</Button>
		</div>
	) : (
		<section>
			<Select
				value={writerId}
				setValue={(v) => setWriterId(v as string)}
				label="Students"
				simpleListBoxId="studentFilter"
				options={writers}
				simpleListBoxClasses="w-64"
			/>
			<Loading loading={writingActivityResult.isLoading} />
			{data && (
				<CourseWritingActivityTable
					activity={data.writingActivity}
					promptSelected={props.selectedPromptId}
					sortField={props.sortField}
					expandedStatusFilter={props.expandedStatusFilter}
					setSelectedPromptId={props.setSelectedPromptId}
					setExpandedStatusFilter={props.setExpandedStatusFilter}
				/>
			)}
		</section>
	);
}
